import { Toast } from '@components/Toast';
import { faFloppyDisk } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { UpdateInstructionVersion } from '@partful/entities/instructions/version';
import { useUpdateInstructionVersion } from '@partful/features/instructions/versions/lib/useUpdateInstructionVersion';
import { VersionModel } from '@partful/instructions-domain-models';
import { Button } from '@partful/shared/ui/Button';
import { useCallback, useState } from 'react';
import { FormState, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OverviewForm } from './OverviewForm/OverviewForm';
import styles from './OverviewTab.module.scss';

type OverviewTabProps = {
  version: VersionModel;
};

export function OverviewTab({ version }: Readonly<OverviewTabProps>) {
  const { t: i18n } = useTranslation();

  const {
    mutate: updateInstructionVersion,
    isLoading: isUpdating,
    isError: isUpdatingError,
    isSuccess: isUpdatingSuccess,
  } = useUpdateInstructionVersion();

  const [formState, setFormState] = useState<FormState<UpdateInstructionVersion>>();
  const handleUpdateFromForm = useCallback<SubmitHandler<UpdateInstructionVersion>>(
    async ({ description }) => {
      if (version) {
        updateInstructionVersion({
          instructionId: version.instructionId,
          versionId: version.id,
          updateArgs: {
            __expectedVersion: version.__version,
            description,
          },
        });
      }
    },
    [updateInstructionVersion, version]
  );

  return (
    <div className={styles.overviewTab}>
      <OverviewForm
        id="overviewForm"
        name="overviewForm"
        version={version}
        onSubmit={handleUpdateFromForm}
        onStateChange={(state) => setFormState(state)}
        disabled={isUpdating}
      />
      <Button
        type="submit"
        form="overviewForm"
        size="large"
        name="instructions_version_overview_save_click"
        disabled={!formState?.isDirty || !formState.isValid || isUpdating}
      >
        <FontAwesomeIcon icon={faFloppyDisk} />
        {i18n(isUpdating ? 'instructions:version.actions.saving' : 'instructions:version.actions.save')}
      </Button>
      <Toast
        dependency={isUpdatingError}
        severity="error"
        title={i18n('instructions:version.actions.saveError.title')}
        message={i18n('instructions:version.actions.saveError.message')}
        testId="update-error-message"
      />
      <Toast
        dependency={isUpdatingSuccess}
        severity="success"
        title={i18n('instructions:version.actions.saveSuccess.title')}
        testId="update-success-message"
      />
    </div>
  );
}
